// Automatic course links in a blog's FAQ section.
//
// Within the FAQ section only (from the first heading that reads "FAQ" /
// "Frequently Asked Questions" up to the next heading), the first mention of
// a real course name is wrapped in a genuine <a href> to that course's page.
// Only ANSWERS are linked:
//   - text inside <a>, <strong>, <b> or a heading is never linked (FAQ
//     questions are written in bold, e.g. "<strong>2. What should a data
//     analytics portfolio project include?</strong>");
//   - a whole <p>/<li> is skipped when it reads as a question: it ends with
//     "?", or starts like "2." and contains no "A:".
// So a course named only in a question is not linked at all, and one named in
// a question and again in a later answer gets its link in that answer.
// Longest course names are tried first (so "Data Analytics Capstone Project"
// wins over "Data Analytics"), matching is case-insensitive on whole words,
// and each course is linked at most once. Runs on the rendered copy of the
// HTML; the saved blog content is never changed.

const FAQ_HEADING_RE = /faq|frequently\s*asked\s*questions/i;
const EXCLUDED_ANCESTORS = "a, strong, b, h1, h2, h3, h4, h5, h6";
const NUMBERED_QUESTION_RE = /^\d+\s*[.)]/;

const escapeRegExp = s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export function isFaqQuestion(el) {
  const text = (el.textContent || "").trim();
  if (text.endsWith("?")) return true;
  return NUMBERED_QUESTION_RE.test(text) && !text.includes("A:");
}

// `courseHref(course)` builds the link target; `doc` is mutated in place.
export function linkifyFaqCourseMentions(doc, courses, courseHref) {
  if (!Array.isArray(courses) || !courses.length) return 0;
  const headings = Array.from(doc.querySelectorAll("h1, h2, h3, h4, h5, h6"));
  const faqHeading = headings.find(heading => FAQ_HEADING_RE.test(heading.textContent || ""));
  if (!faqHeading) return 0;

  const sortedCourses = courses
    .filter(c => c.courseName && c.courseCategory)
    .sort((a, b) => b.courseName.length - a.courseName.length);
  const linkedCourseKeys = new Set();
  const showText = doc.defaultView?.NodeFilter?.SHOW_TEXT ?? 4;

  let node = faqHeading.nextElementSibling;
  while (node && !/^H[1-6]$/.test(node.tagName)) {
    const targets = node.matches?.("p, li") ? [node] : Array.from(node.querySelectorAll?.("p, li") || []);
    targets.forEach(el => {
      if (isFaqQuestion(el)) return;
      for (const course of sortedCourses) {
        const key = `${course.courseCategory}::${course.courseName}`;
        if (linkedCourseKeys.has(key)) continue;
        const re = new RegExp(`\\b${escapeRegExp(course.courseName)}\\b`, "i");
        const walker = doc.createTreeWalker(el, showText);
        let textNode;
        let matched = false;
        while ((textNode = walker.nextNode())) {
          if (textNode.parentElement?.closest(EXCLUDED_ANCESTORS)) continue;
          const match = textNode.textContent.match(re);
          if (!match) continue;
          const before = textNode.textContent.slice(0, match.index);
          const after = textNode.textContent.slice(match.index + match[0].length);
          const anchor = doc.createElement("a");
          anchor.setAttribute("href", courseHref(course));
          anchor.textContent = match[0];
          const parent = textNode.parentNode;
          parent.insertBefore(doc.createTextNode(before), textNode);
          parent.insertBefore(anchor, textNode);
          parent.insertBefore(doc.createTextNode(after), textNode);
          parent.removeChild(textNode);
          matched = true;
          break;
        }
        if (matched) {
          linkedCourseKeys.add(key);
          break;
        }
      }
    });
    node = node.nextElementSibling;
  }
  return linkedCourseKeys.size;
}
